<template>
    <div class="rounded-lgr border border-border bg-white p-3" :class="question.enabled ? '' : 'opacity-60'">
        <div class="flex items-start gap-2">
            <button type="button" class="question-handle cursor-grab text-ink-muted hover:text-ink pt-2" aria-label="Drag to reorder question">
                <i class="bi bi-grip-vertical" aria-hidden="true"></i>
            </button>

            <div class="grow flex flex-col gap-2">
                <textarea
                    :value="question.text"
                    rows="2"
                    placeholder="What should the rater be asked?"
                    :aria-label="`Question ${index + 1} text`"
                    class="w-full px-3 py-2 border border-control-border rounded-lg body-small focus-field"
                    @focus="$emit('focus')"
                    @input="$emit('update', { text: ($event.target as HTMLTextAreaElement).value })"></textarea>

                <div class="flex flex-wrap items-center gap-2">
                    <select
                        :value="question.question_type"
                        :aria-label="`Question ${index + 1} type`"
                        class="px-2 py-2 border border-control-border rounded-lg focus-field details"
                        @change="onTypeChange(($event.target as HTMLSelectElement).value as QuestionType)">
                        <option v-for="option in questionTypes" :key="option.value" :value="option.value">{{ option.label }}</option>
                    </select>

                    <select
                        v-if="question.question_type === 'Score'"
                        :value="question.scale ?? ''"
                        :aria-label="`Question ${index + 1} scale`"
                        class="px-2 py-2 border border-control-border rounded-lg focus-field details"
                        @change="$emit('update', { scale: ($event.target as HTMLSelectElement).value || null })">
                        <option value="">Template default ({{ defaultScaleLabel }})</option>
                        <option v-for="option in scales" :key="option.value" :value="option.value">{{ option.label }}</option>
                    </select>

                    <input
                        :value="question.title"
                        type="text"
                        maxlength="255"
                        placeholder="Eyebrow label (optional)"
                        :aria-label="`Question ${index + 1} label`"
                        class="px-2 py-2 border border-control-border rounded-lg focus-field details w-52"
                        @focus="$emit('focus')"
                        @input="$emit('update', { title: ($event.target as HTMLInputElement).value })" />

                    <label :for="`${uid}-optional`" class="flex items-center gap-2 details text-ink-muted">
                        <input
                            :id="`${uid}-optional`"
                            :checked="question.is_optional"
                            type="checkbox"
                            @change="$emit('update', { is_optional: ($event.target as HTMLInputElement).checked })" />
                        Optional
                    </label>

                    <label :for="`${uid}-enabled`" class="flex items-center gap-2 details text-ink-muted">
                        <input
                            :id="`${uid}-enabled`"
                            :checked="question.enabled"
                            type="checkbox"
                            @change="$emit('update', { enabled: ($event.target as HTMLInputElement).checked })" />
                        Enabled
                    </label>

                    <div class="ml-auto flex items-center gap-1">
                        <button type="button" class="details text-ink-muted hover:text-ink px-2 py-1" @click="$emit('duplicate')">
                            <i class="bi bi-files" aria-hidden="true"></i> Duplicate
                        </button>
                        <BaseTooltip v-if="locked">
                            <template #trigger>
                                <span class="details text-ink-muted px-2 py-1"><i class="bi bi-lock" aria-hidden="true"></i> Answered</span>
                            </template>
                            <template #content>This question already has responses, so it can only be disabled — not deleted.</template>
                        </BaseTooltip>
                        <button v-else type="button" class="details text-red-600 hover:text-red-700 px-2 py-1" @click="$emit('remove')">
                            <i class="bi bi-trash" aria-hidden="true"></i> Delete
                        </button>
                    </div>
                </div>

                <!-- Report-only configuration: how the summary reads this statement.
                     Collapsed by default so a long survey still skims; the badge
                     shows which questions carry it. -->
                <details v-if="question.question_type === 'Score'" class="details text-ink-muted">
                    <summary class="cursor-pointer select-none flex items-center gap-2 py-1">
                        <span>Report interpretation</span>
                        <span
                            v-if="hasInterpretation"
                            class="inline-flex items-center rounded-full bg-surface-sunken text-ink px-2 py-1 details"
                            :data-testid="`interpretation-badge-${question.client_id}`">
                            {{ directionLabel }}
                        </span>
                    </summary>
                    <div class="mt-2 flex flex-col gap-3 pl-1">
                        <label :for="`${uid}-direction`" class="block">
                            <span class="block body-small-bold text-ink mb-1">Strength direction</span>
                            <select
                                :id="`${uid}-direction`"
                                :value="question.strength_direction"
                                class="px-2 py-2 border border-control-border rounded-lg focus-field details bg-white"
                                @change="$emit('update', { strength_direction: ($event.target as HTMLSelectElement).value as StrengthDirection })">
                                <option v-for="option in strengthDirections" :key="option.value" :value="option.value">{{ option.label }}</option>
                            </select>
                            <span class="block details text-ink-muted mt-1">Low means disagreeing is the strength; mixed items never count as a strength on their own.</span>
                        </label>

                        <label v-for="field in interpretationFields" :key="field.name" :for="`${uid}-${field.name}`" class="block">
                            <span class="block body-small-bold text-ink mb-1">{{ field.label }}</span>
                            <textarea
                                :id="`${uid}-${field.name}`"
                                :value="question[field.name]"
                                rows="2"
                                class="w-full px-3 py-2 border border-control-border rounded-lg details bg-white focus-field"
                                @input="$emit('update', { [field.name]: ($event.target as HTMLTextAreaElement).value })"></textarea>
                            <span class="block details text-ink-muted mt-1">{{ field.hint }}</span>
                        </label>
                    </div>
                </details>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import type { BuilderChoice, BuilderQuestion, BuilderScaleOption, QuestionType, StrengthDirection } from "~vue/SurveyBuilder/types";
import { computed, useId } from "vue";

const props = defineProps<{
    question: BuilderQuestion;
    index: number;
    questionTypes: BuilderChoice[];
    scales: BuilderScaleOption[];
    strengthDirections: BuilderChoice[];
    defaultScaleLabel: string;
    /** True when responses exist, which makes the question undeletable. */
    locked: boolean;
}>();

const emit = defineEmits<{ focus: []; duplicate: []; remove: []; update: [changes: Partial<BuilderQuestion>] }>();

const uid = useId();

type InterpretationTextField = "agreement_signals" | "disagreement_signals" | "why_it_matters";

const interpretationFields: { name: InterpretationTextField; label: string; hint: string }[] = [
    { name: "agreement_signals", label: "Agreement signals", hint: "What agreeing tends to indicate. Feeds the summary; never shown verbatim." },
    { name: "disagreement_signals", label: "Disagreement signals", hint: "What disagreeing tends to indicate. Feeds the summary; never shown verbatim." },
    { name: "why_it_matters", label: "Why it matters", hint: "Why this behaviour matters, in the framework's words. Feeds the summary; never shown verbatim." },
];

/** Cleared defaults, so a question that becomes open text carries no report interpretation. */
const NO_INTERPRETATION: Pick<BuilderQuestion, "strength_direction" | InterpretationTextField> = {
    strength_direction: "high",
    agreement_signals: "",
    disagreement_signals: "",
    why_it_matters: "",
};

const hasInterpretation = computed(() => props.question.strength_direction !== "high" || interpretationFields.some((field) => props.question[field.name].trim() !== ""));

const directionLabel = computed(() => props.strengthDirections.find((option) => option.value === props.question.strength_direction)?.label ?? props.question.strength_direction);

function onTypeChange(questionType: QuestionType) {
    emit("update", questionType === "Text" ? { question_type: questionType, ...NO_INTERPRETATION } : { question_type: questionType });
}
</script>
